import { FormItemResponseDTO } from '@/api/base/DTOs';
import { FormField, IFormBase } from '@/forms/typings';
import { formApiAdapterRegistry } from '../registry/form-api-adapter.registry';
import { adaptFormFieldAlertMessageResponse } from '../utilities/adaptFormFieldAlertMessageResponse';
import { adaptFormFieldWarningsResponse } from '../utilities/adaptFormFieldWarningsResponse';
import { adaptFormFieldDeclarationResponse } from '../utilities/adaptFormFieldDeclarationResponse';

/**
 * Adapts a FormItemResponseDTO to a FormField using {@link formApiAdapterRegistry}
 *
 * This is the main entry point for transforming API form item responses
 * into internal FormField representations. It delegates to specific adapters
 * registered in Registry 1 (`form-api-adapter.registry.ts` via `form-api-adapter-registry.init.ts`).
 *
 * @param sectionItemResponse - The form item DTO from the API
 * @param parentFieldId - Optional parent field ID for nested fields
 * @returns The adapted FormField
 * @throws Error if no adapter is registered for the form item type
 */
export const adaptFormItemResponseToFormFields = (
  sectionItemResponse: FormItemResponseDTO,
  parentFieldId?: IFormBase['fieldId'],
): FormField => {
  const adapted = formApiAdapterRegistry.adapt(sectionItemResponse, parentFieldId);

  if (sectionItemResponse.alertMessage) {
    adapted.alertMessage = adaptFormFieldAlertMessageResponse(
      sectionItemResponse.alertMessage,
    );
  }

  const warnings = adaptFormFieldWarningsResponse(sectionItemResponse);

  if (warnings) {
    adapted.warnings = warnings;
  }

  const declaration = adaptFormFieldDeclarationResponse(sectionItemResponse);

  if (declaration) {
    adapted.declaration = declaration;
  }

  if (sectionItemResponse.naDetails) {
    adapted.naDetails = adaptFormItemResponseToFormFields(
      sectionItemResponse.naDetails,
      sectionItemResponse.fieldID,
    );
  }

  return adapted;
};
